import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  onboardingApi,
  OnboardingDocTypeCodes,
  OnboardingStatus,
  type AdminOnboardingDetailsDto,
  type AdminOnboardingDocumentDto,
} from '../api/onboarding';
import { useAdminAuthStore } from '../store/adminAuthStore';
import { formatPublicIdDisplay } from '../utils/formatPublicId';
import { ArrowLeft, User, Building2, FileText, Clock, AlertCircle, LifeBuoy, MessageSquare, Bell } from 'lucide-react';
import { PortalClientDataReadView } from '../components/onboarding/PortalClientDataReadView';
import { AdminSupportTicketModal } from '../components/support/AdminSupportTicketModal';

function ticketTypeLabel(reportType: string) {
  const u = (reportType ?? '').toUpperCase();
  if (u === 'ONBOARDING_ADDITIONAL_INFO') return 'Onboarding (additional info)';
  if (u === 'PAYMENT_ISSUE') return 'Payment issue';
  if (u === 'GENERAL_OTHER') return 'General / other';
  return reportType || '—';
}

function formatGuidShort(id: string) {
  if (!id) return '';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function defaultReminderDatetimeLocal(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function TypeBadge({ applicationType }: { applicationType?: string }) {
  const u = (applicationType ?? '').toUpperCase();
  let label = applicationType ?? '—';
  if (u === 'PRIVATE') label = 'Individual';
  if (u === 'BUSINESS') label = 'Business';
  const cls =
    u === 'BUSINESS'
      ? 'bg-indigo-100 text-indigo-800'
      : u === 'PRIVATE'
        ? 'bg-sky-100 text-sky-800'
        : 'bg-gray-100 text-gray-800';
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toUpperCase();
  const map: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    IN_REVIEW: 'bg-amber-100 text-amber-900',
    ADDITIONAL_INFO_REQUIRED: 'bg-orange-100 text-orange-900',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };
  const cls = map[s] ?? 'bg-gray-100 text-gray-800';
  const label = s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>{label}</span>;
}

export default function OnboardingDetailPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminOnboardingDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [moreInfoComment, setMoreInfoComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [modal, setModal] = useState<null | 'more' | 'reject'>(null);
  const [supportTicketModalId, setSupportTicketModalId] = useState<string | null>(null);
  const [downloadBusyId, setDownloadBusyId] = useState<string | null>(null);
  const ledgerFileInputRef = useRef<HTMLInputElement>(null);
  const adminUser = useAdminAuthStore((s) => s.adminUser);
  const auditUserId = adminUser?.id ?? '';
  const [adminInternalCommentDraft, setAdminInternalCommentDraft] = useState('');
  const [reminderNote, setReminderNote] = useState('');
  const [reminderAtLocal, setReminderAtLocal] = useState(() => defaultReminderDatetimeLocal());

  const load = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError('');
    try {
      const d = await onboardingApi.getDetails(applicationId);
      setDetail(d);
    } catch (e: unknown) {
      setDetail(null);
      setError(e instanceof Error ? e.message : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const comment = detail?.application?.adminInternalComment ?? '';
    setAdminInternalCommentDraft(comment);
  }, [detail?.application?.adminInternalComment, detail?.application?.id]);

  const app = detail?.application;
  const status = (app?.status ?? '').toUpperCase();

  const canAdminEditBusinessOnboarding =
    (app?.applicationType ?? '').toUpperCase() === 'BUSINESS';

  /** After additional info, client resubmits → SUBMITTED again, but `reviewerUserId` stays set (see backend SubmitAsync). */
  const isResumeReview =
    status === OnboardingStatus.Submitted && Boolean(app?.reviewerUserId);

  const actionAvailable = useMemo(
    () => ({
      startReview: status === OnboardingStatus.Submitted,
      approveRejectMore: status === OnboardingStatus.InReview,
      resumeFromAdditionalInfo: status === OnboardingStatus.AdditionalInfoRequired,
    }),
    [status]
  );

  const needsSupportTicket =
    status === OnboardingStatus.AdditionalInfoRequired && (detail?.linkedSupportTickets?.length ?? 0) === 0;

  const closeModal = () => {
    setModal(null);
    setMoreInfoComment('');
    setRejectReason('');
  };

  const run = async (fn: () => Promise<void>) => {
    if (!applicationId) return;
    setBusy(true);
    setBanner(null);
    try {
      await fn();
      setBanner({ type: 'ok', text: 'Saved.' });
      closeModal();
      await load();
    } catch (e: unknown) {
      setBanner({ type: 'err', text: e instanceof Error ? e.message : 'Action failed' });
      await load();
    } finally {
      setBusy(false);
    }
  };

  /** Same as run but does not close draft modals (for file uploads etc.). */
  const runQuiet = async (fn: () => Promise<void>, okMessage: string) => {
    if (!applicationId) return;
    setBusy(true);
    setBanner(null);
    try {
      await fn();
      setBanner({ type: 'ok', text: okMessage });
      await load();
    } catch (e: unknown) {
      setBanner({ type: 'err', text: e instanceof Error ? e.message : 'Action failed' });
    } finally {
      setBusy(false);
    }
  };

  const ensureSupportTicket = () =>
    run(async () => {
      if (!applicationId) return;
      await onboardingApi.ensureAdditionalInfoTicket(applicationId);
    });

  const uploadLedgerDoc = async (file: File) => {
    if (!applicationId || !auditUserId) {
      setBanner({
        type: 'err',
        text: !auditUserId ? 'Admin session missing — please sign in again.' : 'Application id missing.',
      });
      return;
    }
    await runQuiet(async () => {
      await onboardingApi.uploadDocument({
        applicationId,
        documentType: OnboardingDocTypeCodes.Ledger,
        file,
        auditUserId,
      });
    }, 'Ledger file uploaded.');
  };

  const deleteLedgerDoc = async (docId: string) => {
    if (!window.confirm('Delete this internal ledger document?')) return;
    await runQuiet(async () => {
      await onboardingApi.deleteDocument(docId);
    }, 'Document deleted.');
  };

  const saveAdminInternalComment = () =>
    runQuiet(async () => {
      await onboardingApi.updateAdminInternalComment(
        applicationId!,
        adminInternalCommentDraft.trim() === '' ? null : adminInternalCommentDraft.trim(),
      );
    }, 'Internal note saved.');

  const saveReminder = () =>
    runQuiet(async () => {
      const note = reminderNote.trim();
      if (!note) throw new Error('Reminder note is required.');
      const remindAt = new Date(reminderAtLocal).toISOString();
      await onboardingApi.addReminder(applicationId!, { note, remindAt });
      setReminderNote('');
      setReminderAtLocal(defaultReminderDatetimeLocal());
    }, 'Reminder scheduled.');

  const removeReminder = (reminderId: string) => {
    if (!window.confirm('Remove this reminder?')) return;
    void runQuiet(async () => {
      await onboardingApi.deleteReminder(applicationId!, reminderId);
    }, 'Reminder removed.');
  };

  const isLedgerDoc = (d: AdminOnboardingDocumentDto) =>
    (d.documentType ?? '').toUpperCase() === OnboardingDocTypeCodes.Ledger;

  const downloadDoc = useCallback(async (doc: AdminOnboardingDocumentDto) => {
    setDownloadBusyId(doc.id);
    setBanner(null);
    try {
      await onboardingApi.downloadDocument(doc.id, doc.originalFileName || 'document');
      setBanner({ type: 'ok', text: 'Download started.' });
    } catch (e: unknown) {
      setBanner({ type: 'err', text: e instanceof Error ? e.message : 'Download failed' });
    } finally {
      setDownloadBusyId(null);
    }
  }, []);

  if (!applicationId) {
    return (
      <div className="p-6">
        <p className="text-red-700">Missing application id.</p>
        <Link to="/onboarding" className="mt-4 inline-block text-blue-600 underline">
          Back to list
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin mx-auto mt-24 h-10 w-10 rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !detail || !app) {
    return (
      <div className="p-6 max-w-xl">
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Unable to load this application.</p>
            <p className="mt-1 text-sm">{error || 'Unknown error'}</p>
            <button
              type="button"
              className="mt-3 text-sm font-semibold text-blue-700 underline"
              onClick={() => navigate('/onboarding')}
            >
              Back to list
            </button>
          </div>
        </div>
      </div>
    );
  }

  const company = detail.companyInfo;

  return (
    <div className="mx-auto max-w-5xl min-w-0 px-6 py-6">
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/onboarding')}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back to applications
        </button>
      </div>

      {banner ? (
        <div
          className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
            banner.type === 'ok'
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      {/* Application */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-full flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">
                Application <span className="font-mono text-sm text-gray-500">{formatGuidShort(applicationId)}</span>
              </h1>
              <TypeBadge applicationType={app.applicationType} />
              <StatusBadge status={app.status} />
              {canAdminEditBusinessOnboarding ? (
                <Link
                  to={`/onboarding/${applicationId}/edit`}
                  className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-900 hover:bg-indigo-100"
                >
                  Complete onboarding (admin)
                </Link>
              ) : null}
            </div>
            <p className="mt-2 font-mono text-xs text-gray-500 break-all" title={applicationId}>
              {applicationId}
            </p>
            <div className="mt-4 grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
              <span className="sm:col-span-2">
                <span className="text-gray-500">Legal name:</span>{' '}
                <span className="font-medium text-gray-900">{(app.legalName ?? '').trim() || '—'}</span>
              </span>
              <span>
                <span className="text-gray-500">Client ID:</span>{' '}
                <span className="font-mono text-xs font-semibold text-gray-900">
                  {formatPublicIdDisplay(app.userPublicId ?? undefined) || '—'}
                </span>
              </span>
              <span className="text-xs text-gray-500 sm:text-right">Same as portal USER ID</span>
              <span className="sm:col-span-2 font-mono text-xs break-all text-gray-500">
                internal clientId: {app.clientId}
              </span>
              <span className="sm:col-span-2 font-mono text-xs break-all text-gray-500">
                internal userId: {app.userId}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 sm:items-end">
            {actionAvailable.startReview ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => onboardingApi.startReview(applicationId))}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isResumeReview ? 'Continue review' : 'Start review'}
              </button>
            ) : null}
            {actionAvailable.startReview && isResumeReview ? (
              <p className="max-w-xs text-right text-xs text-gray-500">
                Client resubmitted while a reviewer was already assigned — continue the same review flow.
              </p>
            ) : null}
            {actionAvailable.resumeFromAdditionalInfo ? (
              <div className="flex flex-col gap-2 sm:items-end">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void run(() => onboardingApi.startReview(applicationId))}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Resume review
                </button>
                <p className="max-w-xs text-right text-xs text-gray-500">
                  Additional info has been provided — resume review to approve or reject.
                </p>
              </div>
            ) : null}
            {actionAvailable.approveRejectMore ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void run(() => onboardingApi.approve(applicationId))}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setModal('more')}
                  className="rounded-lg border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-900 hover:bg-orange-100 disabled:opacity-50"
                >
                  Request more info
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setModal('reject')}
                  className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-900 hover:bg-red-100 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {needsSupportTicket ? (
          <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-4 text-sm text-amber-950">
            <p className="font-semibold">No support ticket linked to this onboarding</p>
            <p className="mt-1 text-amber-900/95">
              This can happen for requests created before ticketing was enabled, or if ticket creation failed. Create a
              ticket now so the client can reply from the onboarding page and Support.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void ensureSupportTicket()}
              className="mt-3 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
            >
              Create support ticket
            </button>
          </div>
        ) : null}
        {(detail.linkedSupportTickets?.length ?? 0) > 0 ? (
          <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-4 text-sm">
            <div className="flex items-center gap-2 text-sky-950">
              <LifeBuoy className="h-5 w-5 shrink-0 text-sky-700" />
              <p className="font-semibold">Support tickets for this onboarding</p>
            </div>
            <ul className="mt-3 space-y-2">
              {detail.linkedSupportTickets!.map((t) => (
                <li key={t.id} className="min-w-0">
                  <button
                    type="button"
                    className="break-all text-left font-mono text-xs font-semibold text-blue-700 underline hover:text-blue-900"
                    onClick={() => setSupportTicketModalId(t.id)}
                  >
                    {t.id}
                  </button>
                  <span className="text-gray-600">
                    {' '}
                    · {ticketTypeLabel(t.reportType)} · {t.status}
                  </span>
                  <span className="text-gray-500"> · </span>
                  <Link
                    className="text-xs font-medium text-blue-600 underline hover:text-blue-800"
                    to={`/support-tickets?ticket=${encodeURIComponent(t.id)}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open in Support
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {/* Reminders — full width below application so it does not squeeze the header */}
      <section className="mb-8 min-w-0 rounded-xl border border-violet-200 bg-violet-50/60 p-5 shadow-sm">
          <div className="flex items-center gap-2 border-b border-violet-200/80 pb-3">
            <Bell className="h-5 w-5 shrink-0 text-violet-800" />
            <h2 className="font-semibold text-gray-900">Reminders</h2>
          </div>
          <p className="mt-2 text-xs text-violet-950/80">
            At the chosen time, all admins connected to the app receive this via the notifications hub (bell in the header).
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-[1fr,200px] md:items-end">
            <div>
              <label className="block text-xs font-medium text-gray-700" htmlFor="onb-reminder-note">
                Note
              </label>
              <textarea
                id="onb-reminder-note"
                className="mt-1 w-full rounded-lg border border-violet-200 bg-white p-2 text-sm text-gray-900 shadow-inner placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                rows={3}
                maxLength={2000}
                placeholder="What to follow up on…"
                value={reminderNote}
                onChange={(e) => setReminderNote(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700" htmlFor="onb-reminder-at">
                  Remind at
                </label>
                <input
                  id="onb-reminder-at"
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-violet-200 bg-white p-2 text-sm text-gray-900 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  value={reminderAtLocal}
                  onChange={(e) => setReminderAtLocal(e.target.value)}
                  disabled={busy}
                />
              </div>
              <button
                type="button"
                disabled={busy || !reminderNote.trim()}
                className="w-full rounded-lg bg-violet-700 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
                onClick={() => void saveReminder()}
              >
                Schedule reminder
              </button>
            </div>
          </div>

          {(detail.adminReminders?.length ?? 0) > 0 ? (
            <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {detail.adminReminders!.map((r) => {
                const sent = Boolean(r.notificationSentAt);
                return (
                  <li
                    key={r.id}
                    className="rounded-lg border border-violet-100 bg-white/90 p-2.5 text-sm shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span
                        className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          sent ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-900'
                        }`}
                      >
                        {sent ? 'Sent' : 'Scheduled'}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                        onClick={() => removeReminder(r.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <p className="mt-1 text-xs font-medium text-gray-900">{new Date(r.remindAt).toLocaleString()}</p>
                    <p className="mt-1 max-w-full min-w-0 whitespace-pre-wrap break-words text-gray-700 [overflow-wrap:anywhere]">
                      {r.note}
                    </p>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-4 text-xs text-gray-500">No reminders yet.</p>
          )}
      </section>

      <div className="grid min-w-0 gap-6 lg:grid-cols-2">
        <section className="min-w-0 rounded-xl border border-amber-200 bg-amber-50/50 p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center gap-2 border-b border-amber-200/80 pb-3">
            <MessageSquare className="h-5 w-5 text-amber-800" />
            <h2 className="font-semibold text-gray-900">Comments, status history & internal note</h2>
          </div>

          {app.additionalInfoComment ? (
            <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
              <p className="font-semibold">Additional info (from review)</p>
              <p className="mt-1 max-w-full min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {app.additionalInfoComment}
              </p>
            </div>
          ) : null}
          {app.rejectionReason ? (
            <div className="mt-4 rounded-lg border border-red-100 bg-red-50/90 px-4 py-3 text-sm text-red-950">
              <p className="font-semibold">Rejection reason (shown on portal)</p>
              <p className="mt-1 max-w-full min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {app.rejectionReason}
              </p>
            </div>
          ) : null}

          {(detail.statusHistory?.length ?? 0) > 0 ? (
            <div className="mt-6">
              <div className="flex items-center gap-2 pb-2">
                <Clock className="h-4 w-4 text-amber-900/70" />
                <h3 className="text-sm font-semibold text-gray-900">Status transitions</h3>
              </div>
              <ol className="space-y-3">
                {[...(detail.statusHistory ?? [])]
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((h) => (
                    <li key={h.id} className="border-l-2 border-amber-300/80 pl-4 text-sm">
                      <p className="font-medium text-gray-900">
                        {(h.fromStatus ?? '∅').replace(/_/g, ' ')} → {h.toStatus.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(h.createdAt).toLocaleString()} · by {formatGuidShort(h.changedByUserId ?? '') || 'system'}
                      </p>
                      {h.comment ? (
                        <p className="mt-1 max-w-full min-w-0 whitespace-pre-wrap break-words text-gray-700 [overflow-wrap:anywhere]">
                          {h.comment}
                        </p>
                      ) : null}
                    </li>
                  ))}
              </ol>
            </div>
          ) : null}

          <div className="mt-8 border-t border-amber-200/80 pt-5">
            <h3 className="text-sm font-semibold text-gray-900">Internal admin note (team only)</h3>
            <p className="mt-1 text-xs text-amber-950/85">
              Not shown to clients on the portal or in onboarding details APIs. Every save stores a snapshot in the history
              below (newest first).
            </p>
            {(detail.adminInternalCommentHistory?.length ?? 0) > 0 ? (
              <div className="mt-4 rounded-lg border border-amber-200/90 bg-white/70 p-4">
                <p className="text-xs font-semibold text-amber-950">Internal note history</p>
                <ol className="mt-3 max-h-[min(24rem,50vh)] space-y-3 overflow-y-auto pr-1">
                  {detail.adminInternalCommentHistory!.map((row) => (
                    <li key={row.id} className="border-l-2 border-amber-400/80 pl-3 text-sm">
                      <p className="text-xs text-gray-500">
                        {new Date(row.createdAt).toLocaleString()}
                        {row.createdByUserId ? ` · ${formatGuidShort(row.createdByUserId)}` : ''}
                      </p>
                      {row.comment != null && row.comment.trim() !== '' ? (
                        <p className="mt-1 max-w-full min-w-0 whitespace-pre-wrap break-words text-gray-800 [overflow-wrap:anywhere]">
                          {row.comment}
                        </p>
                      ) : (
                        <p className="mt-1 text-sm italic text-gray-500">(Cleared)</p>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
            <textarea
              className="mt-3 w-full rounded-lg border border-amber-200 bg-white p-3 text-sm text-gray-900 shadow-inner placeholder:text-gray-400 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
              rows={5}
              maxLength={20000}
              placeholder="Notes for your team: context, follow-ups, risk flags…"
              value={adminInternalCommentDraft}
              onChange={(e) => setAdminInternalCommentDraft(e.target.value)}
              disabled={busy}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy}
                className="rounded-lg bg-amber-800 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-900 disabled:opacity-50"
                onClick={() => void saveAdminInternalComment()}
              >
                Save note
              </button>
              <button
                type="button"
                disabled={busy}
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-950 hover:bg-amber-100/80 disabled:opacity-50"
                onClick={() => setAdminInternalCommentDraft(app?.adminInternalComment ?? '')}
              >
                Reset
              </button>
              <span className="text-xs text-gray-500">{adminInternalCommentDraft.length} / 20000</span>
            </div>
          </div>
        </section>

        <PortalClientDataReadView
          applicationType={app.applicationType}
          extendedQuestionnaireJson={app.extendedQuestionnaireJson}
          directorVerificationJson={app.directorVerificationJson}
        />

        <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
            <Building2 className="h-5 w-5 text-gray-500" />
            <h2 className="font-semibold text-gray-900">Company (API snapshot)</h2>
          </div>
          {!company ? (
            <p className="mt-4 text-sm text-gray-500">No company profile saved on the server yet.</p>
          ) : (
            <dl className="mt-4 space-y-3 text-sm">
              <div className="min-w-0">
                <dt className="text-xs uppercase text-gray-500">Legal name</dt>
                <dd className="mt-0.5 max-w-full min-w-0 break-words font-medium text-gray-900">{company.companyName}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs uppercase text-gray-500">Registration</dt>
                <dd className="mt-0.5 max-w-full min-w-0 break-words">{company.registrationNumber}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs uppercase text-gray-500">Country</dt>
                <dd className="mt-0.5 max-w-full min-w-0 break-words">{company.countryOfIncorporation}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs uppercase text-gray-500">Activity</dt>
                <dd className="mt-0.5 max-w-full min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {company.businessActivity}
                </dd>
              </div>
              {company.website ? (
                <div className="min-w-0">
                  <dt className="text-xs uppercase text-gray-500">Website</dt>
                  <dd className="mt-0.5 max-w-full min-w-0 break-all font-mono text-xs">{company.website}</dd>
                </div>
              ) : null}
            </dl>
          )}
        </section>

        <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
            <User className="h-5 w-5 text-gray-500" />
            <h2 className="font-semibold text-gray-900">
              Persons (API-linked) <span className="text-gray-500">({detail.persons.length})</span>
            </h2>
          </div>
          {detail.persons.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">None.</p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100">
              {detail.persons.map((p) => (
                <li key={p.id} className="py-3 text-sm first:pt-0">
                  <p className="font-medium text-gray-900">{p.fullName}</p>
                  <p className="mt-1 text-xs text-gray-600">
                    {p.role.replace(/_/g, ' ')} · {p.nationality} · ID: {p.idNumber}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">Verification: {p.verificationStatus || '—'}</p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">
                Documents <span className="text-gray-500">({detail.documents.length})</span>
              </h2>
            </div>
            <div className="flex flex-col items-end gap-1">
              <input
                ref={ledgerFileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void uploadLedgerDoc(f);
                }}
              />
              <button
                type="button"
                disabled={busy || !auditUserId}
                title={!auditUserId ? 'Admin session required' : undefined}
                onClick={() => ledgerFileInputRef.current?.click()}
                className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-50"
              >
                Upload ledger (admin only, hidden from client)
              </button>
              <p className="max-w-md text-right text-xs text-gray-500">
                API type: <span className="font-mono">LEDGER</span>. PDF or image, up to 28 MB.
              </p>
            </div>
          </div>
          {detail.documents.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">No documents.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium uppercase text-gray-500">
                    <th className="py-2 pr-4">File</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Visibility</th>
                    <th className="py-2 pr-4">Size</th>
                    <th className="py-2 pr-4">Person ID</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="max-w-[min(28rem,calc(100vw-12rem))] py-2 pr-4 align-top break-all">{doc.originalFileName}</td>
                      <td className="py-2 pr-4 align-top break-words text-gray-600">
                        {doc.documentType.replace(/_/g, ' ')}
                      </td>
                      <td className="py-2 pr-4 align-top">
                        {isLedgerDoc(doc) ? (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                            Internal
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">Client</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-4">{Math.round(doc.fileSize / 1024)} KB</td>
                      <td className="max-w-[12rem] break-all py-2 pr-4 font-mono text-xs">{doc.personId || '—'}</td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          disabled={downloadBusyId === doc.id}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          onClick={() => void downloadDoc(doc)}
                        >
                          {downloadBusyId === doc.id ? 'Downloading…' : 'Download'}
                        </button>
                        {isLedgerDoc(doc) ? (
                          <button
                            type="button"
                            disabled={busy}
                            className="ml-3 text-xs font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                            onClick={() => void deleteLedgerDoc(doc.id)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </div>

      {modal === 'more' ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Request additional information</h3>
            <p className="mt-2 text-sm text-gray-600">This moves the application to Additional info required.</p>
            <textarea
              className="mt-4 w-full rounded-lg border border-gray-300 p-3 text-sm"
              rows={4}
              placeholder="Explain what you need from the client..."
              value={moreInfoComment}
              onChange={(e) => setMoreInfoComment(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || !moreInfoComment.trim()}
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                onClick={() =>
                  run(() => onboardingApi.requestAdditionalInfo(applicationId, moreInfoComment.trim()))
                }
              >
                Send request
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminSupportTicketModal
        ticketId={supportTicketModalId}
        open={supportTicketModalId != null}
        onClose={() => setSupportTicketModalId(null)}
        afterChange={() => void load()}
      />

      {modal === 'reject' ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Reject application</h3>
            <p className="mt-2 text-sm text-gray-600">The client will see this reason in the portal.</p>
            <textarea
              className="mt-4 w-full rounded-lg border border-gray-300 p-3 text-sm"
              rows={4}
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || !rejectReason.trim()}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                onClick={() => run(() => onboardingApi.reject(applicationId, rejectReason.trim()))}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
